import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type {
  AplicarPlanoModeloPayload,
  AplicarPlanosEmLotePayload,
  AplicarPlanosEmLoteResultado,
  AtividadePMOC,
  AtividadePMOCUpdateInput,
  MedicaoEsperada,
  MedicaoEsperadaCreateInput,
  PlanoPMOC,
  PlanoPMOCUpdateInput,
} from '../types/pmocPlanos'
import type { PaginatedResponse } from '../types'

/* ---------- Snapshots PlanoPMOC ---------- */

export function usePlanosPmocList(pmocId: string) {
  return useQuery({
    queryKey: ['planos-pmoc', pmocId],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<PlanoPMOC>>(
        `/pmoc/${pmocId}/planos-snapshot/`,
      )
      return r.data
    },
    enabled: !!pmocId,
  })
}

export function usePlanoPmocDetalhe(planoPmocId: string) {
  return useQuery({
    queryKey: ['planos-pmoc-detalhe', planoPmocId],
    queryFn: async () => {
      const r = await api.get<PlanoPMOC>(`/planos-pmoc/${planoPmocId}/`)
      return r.data
    },
    enabled: !!planoPmocId,
  })
}

export function usePlanoPmocUpdate(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: PlanoPMOCUpdateInput
    }) => {
      const r = await api.patch<PlanoPMOC>(`/planos-pmoc/${id}/`, data)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planos-pmoc', pmocId] })
    },
  })
}

export function usePlanoPmocDelete(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/planos-pmoc/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planos-pmoc', pmocId] }),
  })
}

export function usePlanoPmocDesvincularEquipamento(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ planoId, equipamentoId }: { planoId: string; equipamentoId: string }) =>
      api.delete(`/planos-pmoc/${planoId}/equipamentos/${equipamentoId}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planos-pmoc', pmocId] }),
  })
}

export function usePlanoPmocPersonalizarEquipamento(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ planoId, equipamentoId }: { planoId: string; equipamentoId: string }) => {
      const r = await api.post<PlanoPMOC>(
        `/planos-pmoc/${planoId}/equipamentos/${equipamentoId}/personalizar/`,
      )
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planos-pmoc', pmocId] }),
  })
}

export function useAplicarPlanoModelo(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: AplicarPlanoModeloPayload) => {
      const r = await api.post<PlanoPMOC>(
        `/pmoc/${pmocId}/aplicar-plano-modelo/`,
        payload,
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planos-pmoc', pmocId] })
      qc.invalidateQueries({ queryKey: ['pmoc', pmocId] })
      qc.invalidateQueries({ queryKey: ['ocorrencias', pmocId] })
    },
  })
}

export function useAplicarPlanosEmLote(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: AplicarPlanosEmLotePayload) => {
      const r = await api.post<AplicarPlanosEmLoteResultado>(
        `/pmoc/${pmocId}/aplicar-planos-em-lote/`,
        payload,
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planos-pmoc', pmocId] })
    },
  })
}

/* ---------- AtividadePMOC ---------- */

export function useAtividadesPmocList(planoPmocId: string) {
  return useQuery({
    queryKey: ['atividades-pmoc', planoPmocId],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<AtividadePMOC>>(
        `/planos-pmoc/${planoPmocId}/atividades/`,
      )
      return r.data
    },
    enabled: !!planoPmocId,
  })
}

export function useAtividadePmocUpdate(planoPmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: AtividadePMOCUpdateInput
    }) => {
      const r = await api.patch<AtividadePMOC>(`/atividades-pmoc/${id}/`, data)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['atividades-pmoc', planoPmocId] })
      qc.invalidateQueries({ queryKey: ['planos-pmoc'] })
    },
  })
}

export function useAtividadePmocDelete(planoPmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/atividades-pmoc/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['atividades-pmoc', planoPmocId] })
      qc.invalidateQueries({ queryKey: ['planos-pmoc'] })
    },
  })
}

export function useAtividadePmocRegerar(planoPmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (atividadeId: string) => {
      const r = await api.post<{
        atividade: string
        ocorrencias_removidas: number
        ocorrencias_criadas: number
      }>(`/atividades-pmoc/${atividadeId}/regerar-ocorrencias/`)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['atividades-pmoc', planoPmocId] })
      qc.invalidateQueries({ queryKey: ['ocorrencias'] })
    },
  })
}

/* ---------- MedicaoEsperada ---------- */

export function useMedicoesEsperadasList(atividadeId: string) {
  return useQuery({
    queryKey: ['medicoes-esperadas', atividadeId],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<MedicaoEsperada>>(
        `/atividades-pmoc/${atividadeId}/medicoes-esperadas/`,
      )
      return r.data
    },
    enabled: !!atividadeId,
  })
}

export function useMedicaoEsperadaCreate(atividadeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: MedicaoEsperadaCreateInput) => {
      const r = await api.post<MedicaoEsperada>(
        `/atividades-pmoc/${atividadeId}/medicoes-esperadas/`,
        data,
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['medicoes-esperadas', atividadeId] })
      qc.invalidateQueries({ queryKey: ['atividades-pmoc'] })
    },
  })
}

export function useMedicaoEsperadaUpdate(atividadeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<MedicaoEsperadaCreateInput>
    }) => {
      const r = await api.patch<MedicaoEsperada>(`/medicoes-esperadas/${id}/`, data)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['medicoes-esperadas', atividadeId] })
      qc.invalidateQueries({ queryKey: ['atividades-pmoc'] })
    },
  })
}

export function useMedicaoEsperadaDelete(atividadeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/medicoes-esperadas/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['medicoes-esperadas', atividadeId] })
      qc.invalidateQueries({ queryKey: ['atividades-pmoc'] })
    },
  })
}
